import { useState } from "react";
import { api } from "../api";
import { haptic } from "../telegram";
import type { Me } from "../types";
import { PageDialog } from "./ReferenceUI";

type Photo = NonNullable<Me["profile"]>["photos"][number];

const statuses: Record<string, string> = {
  pending: "Проверяется",
  approved: "Одобрено",
  rejected: "Отклонено",
};

export function PhotoManagement({
  photos,
  onRefresh,
  onClose,
}: {
  photos: Photo[];
  onRefresh(): Promise<void>;
  onClose(): void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <PageDialog title="Мои фотографии" onClose={onClose} busy={busy}>
      <PhotoManagementContent photos={photos} onRefresh={onRefresh} onBusyChange={setBusy} />
    </PageDialog>
  );
}
export function PhotoManagementContent({
  photos,
  onRefresh,
  onBusyChange,
}: {
  photos: Photo[];
  onRefresh(): Promise<void>;
  onBusyChange?(busy: boolean): void;
}) {
  const [busy, updateBusy] = useState(false);
  const setBusy = (next: boolean) => {
    updateBusy(next);
    onBusyChange?.(next);
  };
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [error, setError] = useState("");
  const perform = async (path: string, method: "PUT" | "DELETE", body?: object) => {
    if (busy) return false;
    setBusy(true);
    setError("");
    try {
      await api(path, { method, ...(body ? { body: JSON.stringify(body) } : {}) });
      await onRefresh();
      haptic("success");
      return true;
    } catch {
      setError("Изменения не сохранились. Проверьте соединение и повторите попытку.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const move = async (index: number, delta: -1 | 1) => {
    const destination = index + delta;
    if (destination < 0 || destination >= photos.length) return;
    const ids = photos.map((photo) => photo.id);
    const currentId = ids[index];
    const otherId = ids[destination];
    if (!currentId || !otherId) return;
    ids[index] = otherId;
    ids[destination] = currentId;
    await perform("/v1/me/photos/order", "PUT", { photoIds: ids });
  };

  return (
    <section className="profile-media-panel">
      <p className="photo-manager-description">
        До 6 фотографий. Главное фото нужно для показа анкеты. Назначить главным можно только одобренный снимок. Новое
        фото проходит модерацию.
      </p>
      <label className="reference-upload">
        {busy ? "Загружаем…" : photos.length >= 6 ? "Добавлено 6 фотографий" : "+ Добавить фотографию"}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          aria-label="Добавить фотографию"
          disabled={busy || photos.length >= 6}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.currentTarget.value = "";
            if (!file || busy) return;
            if (file.size > 10 * 1024 * 1024) {
              setError("Выберите фотографию до 10 МБ.");
              return;
            }
            setBusy(true);
            setError("");
            const form = new FormData();
            form.append("photo", file);
            void api("/v1/me/photos", { method: "POST", body: form })
              .then(onRefresh)
              .catch(() => setError("Не удалось загрузить фотографию. Используйте JPG, PNG или WebP до 10 МБ."))
              .finally(() => setBusy(false));
          }}
        />
      </label>
      <div className="photo-manager-list">
        {photos.map((photo, index) => (
          <div className="photo-manager-item" key={photo.id}>
            <img src={photo.url} alt={`Фотография ${index + 1}`} />
            <div className="photo-manager-details">
              <strong>{photo.isPrimary ? "Главное фото" : `Фото ${index + 1}`}</strong>
              <small>{statuses[photo.moderationStatus] ?? "Статус неизвестен"}</small>
              <div className="photo-manager-controls">
                <button
                  type="button"
                  disabled={busy || index === 0}
                  aria-label={`Переместить фото ${index + 1} выше`}
                  onClick={() => void move(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  disabled={busy || index === photos.length - 1}
                  aria-label={`Переместить фото ${index + 1} ниже`}
                  onClick={() => void move(index, 1)}
                >
                  ↓
                </button>
                {!photo.isPrimary && photo.moderationStatus === "approved" && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void perform(`/v1/me/photos/${photo.id}/primary`, "PUT")}
                  >
                    Сделать главным
                  </button>
                )}
                {!photo.isPrimary &&
                  (confirmDelete === photo.id ? (
                    <>
                      <span className="photo-manager-confirm-title">Удалить фото?</span>
                      <button type="button" disabled={busy} onClick={() => setConfirmDelete(null)}>
                        Нет
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          void (async () => {
                            const done = await perform(`/v1/me/photos/${photo.id}`, "DELETE");
                            if (done) setConfirmDelete(null);
                          })();
                        }}
                      >
                        Да, удалить
                      </button>
                    </>
                  ) : (
                    <button type="button" disabled={busy} onClick={() => setConfirmDelete(photo.id)}>
                      Удалить
                    </button>
                  ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {error && (
        <p className="photo-manager-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
